
import React from 'react';
import { useTheme } from "@/contexts/ThemeContext";

interface CpvBarChartProps {
  value: number | string;
  maxValue: number;
  className?: string;
  shouldAnimate?: boolean;
}

export const CpvBarChart = ({ 
  value, 
  maxValue, 
  className = "",
  shouldAnimate = false 
}: CpvBarChartProps) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  // Convert value to number if it's a string
  const numericValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
  
  // Calculate percentage width (minimum 5% for visibility when value > 0)
  const percentage = maxValue > 0 ? Math.max((numericValue / maxValue) * 100, numericValue > 0 ? 5 : 0) : 0;
  
  // Generate unique animation delay for staggered effect
  const animationDelay = shouldAnimate ? Math.random() * 300 : 0;
  
  // Determine color based on CPV amount
  const getBarColor = (cpv: number) => {
    if (cpv >= 0.2) {
      return isLightMode ? 'bg-cyan-500' : 'bg-cyan-600'; // Mint blue for 0.2+
    } else if (cpv >= 0.03) {
      return isLightMode ? 'bg-blue-300' : 'bg-blue-400'; // Light blue for 0.03-0.1
    } else if (cpv > 0) {
      return isLightMode ? 'bg-blue-100' : 'bg-blue-200'; // Very light blue for 0-0.03
    }
    return isLightMode ? 'bg-white border border-gray-200' : 'bg-gray-600'; // White for 0
  };
  
  const barColor = getBarColor(numericValue);
  
  // Format CPV value to 4 decimal places
  const formatCpvValue = (cpv: number | string): string => {
    const numCpv = typeof cpv === 'string' ? parseFloat(cpv) || 0 : cpv;
    return numCpv.toFixed(4);
  };
  
  return (
    <div className={`relative flex items-center ${className}`}>
      {/* Background bar */}
      <div className={`absolute inset-0 rounded overflow-hidden ${isLightMode ? 'bg-gray-100' : 'bg-gray-800'}`} />
      
      {/* Progress bar with color categorization and subtle stripes */}
      <div 
        className={`absolute left-0 top-0 h-full rounded overflow-hidden transition-all duration-1000 ease-out ${barColor}`}
        style={{ 
          width: shouldAnimate ? `${percentage}%` : `${percentage}%`,
          backgroundImage: isLightMode 
            ? `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 8px,
                rgba(255, 255, 255, 0.15) 8px,
                rgba(255, 255, 255, 0.15) 16px
              )`
            : `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 8px,
                rgba(255, 255, 255, 0.1) 8px,
                rgba(255, 255, 255, 0.1) 16px
              )`,
          animation: shouldAnimate 
            ? `growWidthCpv 1000ms ease-out ${animationDelay}ms both, moveStripesCpv 3s linear infinite`
            : 'none'
        }}
      />
      
      {/* Value text */}
      <div className={`relative z-10 px-3 py-1 font-medium ${
        isLightMode ? 'text-gray-900' : 'text-gray-100'
      }`}>
        ${formatCpvValue(value)}
      </div>
      
      {/* Global CSS for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes growWidthCpv {
            from {
              width: 0%;
            }
            to {
              width: ${percentage}%;
            }
          }
          
          @keyframes moveStripesCpv {
            0% {
              background-position: 0 0;
            }
            100% {
              background-position: 32px 0;
            }
          }
        `
      }} />
    </div>
  );
};
